/**
 * The "single" look of the train results (Essenziale): one train at a time, large in the middle,
 * its neighbours peeking at the sides — a tap on a neighbour or a swipe (the card following the finger) brings it to the middle. When the train
 * in focus is on another backend page, the backend is told (go_next_page / go_prev_page), so what the
 * voice calls "the first one" is still what the screen shows.
 */
import { useState } from 'react';
import { formatMoney } from '@platform/i18n/translator';
import { useUi } from '@platform/react/hooks';
import { Icon } from '@platform/ui-kit/Icon';
import { useSwipe } from '@platform/ui-kit/swipe';
import type { WidgetProps } from '@platform/widgets/contract';
import { classRows, durationLabel, trainTypeLabel } from '../fares';
import type { Train } from '../session';
import { RESULTS_PER_PAGE, type TrainResultsProps } from './results-schema';
import { ChangesButton, ItineraryCover } from './TrainItinerary';
import { changesOf, trainItinerary } from './itinerary';
import css from './TrainSingle.module.css';

export function TrainSingle({ props, ctx, api }: WidgetProps<TrainResultsProps>) {
  const { t, language } = ctx;
  const first = props.selectedIdx !== null ? props.selectedIdx - 1 : props.page * RESULTS_PER_PAGE;
  const openDetail = useUi((u) => u.detail?.item ?? null);
  const [seen, setSeen] = useState<{ page: number; focus: number; detail?: string | null }>({ page: props.page, focus: first });
  // The backend moved the page (voice: "show me the next ones"): focus its first train.
  let focus = seen.focus;
  if (seen.page !== props.page) {
    focus = props.page * RESULTS_PER_PAGE;
    setSeen({ page: props.page, focus, detail: seen.detail });
  }
  // A reply opened a train's detail (the voice: "che cambi ha il terzo?"): that train comes to the middle.
  const detailOf = props.trains.findIndex((tr) => tr.train_id === openDetail);
  if (detailOf !== -1 && detailOf !== focus && seen.detail !== openDetail) {
    focus = detailOf;
    setSeen({ page: props.page, focus, detail: openDetail });
  }
  focus = Math.max(0, Math.min(focus, props.trains.length - 1));
  const money = (n: number) => formatMoney(n, language);

  const move = (to: number) => {
    if (to < 0 || to >= props.trains.length) return;
    const page = Math.floor(to / RESULTS_PER_PAGE);
    setSeen({ page: props.page, focus: to, detail: seen.detail });
    if (page > props.page) api.press('go_next_page');
    else if (page < props.page) api.press('go_prev_page');
  };
  // the card follows the finger; the swipe brings its neighbour to the middle
  const { area, track: card } = useSwipe({
    enabled: api.swipe && !api.readOnly && props.trains.length > 1,
    follow: true,
    at: focus,
    can: (direction) => (direction === 'next' ? focus < props.trains.length - 1 : focus > 0),
    onSwipe: (direction) => move(direction === 'next' ? focus + 1 : focus - 1),
  });

  if (props.trains.length === 0) return <div className={css.empty}>{t('trains.results.empty')}</div>;
  const train = props.trains[focus]!;
  const idx = focus + 1;
  return (
    <div className={css.stage} {...area}>
      <Peek train={props.trains[focus - 1]} side="left" onTap={() => move(focus - 1)} />
      <article ref={card} className={`${css.card} ${props.selectedIdx === idx ? css.selected : ''}`} key={train.train_id + train.departure_time}>
        <button className={css.head} onClick={() => api.press('select_train', { idx })} disabled={train.available_seats === 0}>
          <span className={css.times}>{train.departure_time}<Icon name="next" size={36} />{train.arrival_time}</span>
          <span className={css.meta}>
            {trainTypeLabel(train, t('trains.results.high_speed'))} {train.train_id} · {durationLabel(train.duration_min)}{changesOf(train) === 0 && ` · ${t('trains.results.direct')}`}
          </span>
        </button>
        {changesOf(train) > 0 && <ChangesButton of={trainItinerary(train, idx)} api={api} t={t} look="chip" />}
        <div className={css.classes}>
          {classRows(train.class_prices).slice(0, 4).map((row) => (
            <button key={row.name} className={css.class} disabled={!row.available}
              onClick={() => api.press('select_class', row.tiers[0] ? { idx, class: row.name, fare: row.tiers[0].fare } : { idx, class: row.name })}>
              <span>{row.name}</span>
              <strong>{row.available ? `${row.fromPrice ? `${t('trains.results.from')} ` : ''}${money(row.price)}` : t('trains.results.sold_out')}</strong>
            </button>
          ))}
        </div>
        {train.blocked_reason && <p className={css.blocked}>{t(`trains.results.blocked.${train.blocked_reason}`)}</p>}
        <ItineraryCover of={trainItinerary(train, idx)} t={t} closeLabel={t('overlay.close')} look="timeline" />
      </article>
      <Peek train={props.trains[focus + 1]} side="right" onTap={() => move(focus + 1)} />
      <p className={css.count}>{t('trains.results.position', { n: idx, total: props.trains.length })}</p>
    </div>
  );
}

function Peek({ train, side, onTap }: { train: Train | undefined; side: 'left' | 'right'; onTap: () => void }) {
  if (!train) return <span className={`${css.peek} ${css[side]} ${css.ghost}`} />;
  return (
    <button className={`${css.peek} ${css[side]}`} onClick={onTap} aria-label={`${train.departure_time} → ${train.arrival_time}`}>
      <span>{train.departure_time}</span>
    </button>
  );
}
